/**
 * Global confirm-transaction dialog.
 *
 * Mount once via `WalletProvider`. Subscribes to `useConfirmTxStore` and
 * renders Confirm/Cancel for whatever payload the current submit pipeline
 * pushed via `requestTransactionConfirmation`.
 *
 * After Confirm, the wallet still pops its own ceremony (WebAuthn
 * fingerprint / Ledger device approval) — this dialog is the
 * application-level "is this what you mean to sign?" gate that runs *before*
 * the system ceremony.
 *
 * Phase 6 slice 4: replaces the legacy auto-decode TransactionGuard pattern
 * (which depended on the deleted `transaction-guard-store` and the equally
 * deleted `transaction-decoder` lib). Pipelines now declare their intent
 * via an explicit `requestTransactionConfirmation({ title, details })` call;
 * the dialog stays presentational with no knowledge of the underlying
 * instructions. Mirrors the detrust-oracle ConfirmTransactionDialog.
 */

import { Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useConfirmTxStore } from "../store/confirm-tx-store";

export function TransactionConfirmationDialog() {
  const { t } = useTranslation();
  const open = useConfirmTxStore((s) => s.open);
  const payload = useConfirmTxStore((s) => s.payload);
  const confirm = useConfirmTxStore((s) => s.confirm);
  const cancel = useConfirmTxStore((s) => s.cancel);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) cancel();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-brand-primary" />
            {payload?.title ?? t("wallet.txConfirm.defaultTitle")}
          </DialogTitle>
          {payload?.description && (
            <DialogDescription>{payload.description}</DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-3 rounded-lg border border-border-card bg-bg-light p-4 text-sm">
          {(payload?.items ?? []).length > 0 && (
            <ul className="space-y-2">
              {(payload?.items ?? []).map((item, idx) => (
                <li
                  key={`${item.title}-${idx}`}
                  className="rounded-md border border-border-card bg-white px-3 py-2"
                >
                  <div className="font-medium text-text-primary break-words">
                    {item.title}
                  </div>
                  {item.subtitle && (
                    <div
                      className={
                        "mt-0.5 font-mono text-xs " +
                        (item.tone === "yes"
                          ? "text-color-yes"
                          : item.tone === "no"
                            ? "text-color-no"
                            : "text-text-muted")
                      }
                    >
                      {item.subtitle}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {(payload?.items ?? []).length > 0 &&
            (payload?.details ?? []).length > 0 && (
              <div className="border-t border-border-card" />
            )}

          {(payload?.details ?? []).length > 0 && (
            <dl className="space-y-2">
              {(payload?.details ?? []).map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between gap-4"
                >
                  <dt className="text-text-muted">{row.label}</dt>
                  <dd className="break-all text-right font-mono text-text-primary">
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        <p className="text-xs text-text-muted">{t("wallet.txConfirm.hint")}</p>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={cancel}>
            {t("wallet.txConfirm.cancel")}
          </Button>
          <Button
            onClick={confirm}
            className="bg-brand-primary hover:bg-brand-primary/90"
          >
            {t("wallet.txConfirm.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
